/** Live entities, grouped the way the platform addresses them: `subject.facet.path`. */

import { currentAttribute, type StateEntity } from "@/lib/runtime";

/** One modifier contributing to one attribute. RUN-044's own three fields, nothing derived. */
export interface ModifierReading {
  op: string;
  magnitude: number;
  source: string;
}

/** One attribute reading: the base that was written and the current value RUN-044 aggregates. */
export interface AttributeReading {
  name: string;
  base: number;
  current: number;
  min: number | null;
  max: number | null;
  modifiers: ModifierReading[];
}

/** One running effect instance, RUN-002's own fields shaped for display. */
export interface EffectReading {
  source: string;
  name: string;
  appliedBy: string;
  /** Whether another activity applied this, so its `appliedBy` is worth naming. */
  foreign: boolean;
  stacks: number;
  expiresAt: number | null;
  suspended: boolean;
}

/** One entity a mount is holding, carrying the address its group was keyed by. */
export interface StateRow {
  key: string;
  id: string;
  type: string;
  address: string;
  /** The customer's name for the added activity holding it. */
  from: string;
  tags: string[];
  attributes: AttributeReading[];
  text: { name: string; value: string }[];
  effects: EffectReading[];
  /** Whether the entity named a scope and a facet, so the address is the whole grammar. */
  addressed: boolean;
}

/** `subject.facet.path`, read off the entity and never assembled from a table. */
export function addressOf(entity: StateEntity): string {
  const parts = [entity.scope ?? "", entity.facet ?? "", entity.type].filter((one) => one !== "");
  return parts.join(".");
}

function rowOf(entity: StateEntity, from: string, mountId: string): StateRow {
  return {
    key: `${mountId}:${entity.id}`,
    id: entity.id,
    type: entity.type,
    address: addressOf(entity),
    from,
    tags: [...entity.tags.tags],
    attributes: Object.entries(entity.attributes).map(([name, held]) => ({
      name,
      base: held.value,
      current: currentAttribute(entity, name),
      min: held.min,
      max: held.max,
      modifiers: (entity.modifiers?.[name] ?? []).map((one) => ({
        op: one.op,
        magnitude: one.magnitude,
        source: one.source,
      })),
    })),
    text: Object.entries(entity.text_attributes ?? {}).map(([name, held]) => ({
      name,
      value: held.value,
    })),
    effects: Object.entries(entity.effects ?? {}).map(([source, active]) => ({
      source,
      name: active.name,
      appliedBy: active.applied_by,
      foreign: active.applied_by !== entity.activity_id,
      stacks: active.stacks ?? 1,
      expiresAt: active.expires_at ?? null,
      suspended: active.suspended ?? false,
    })),
    addressed: (entity.scope ?? "") !== "" && (entity.facet ?? "") !== "",
  };
}

/** What a reading contributes: its entities, under the name the customer gave the activity. */
export interface EntityReading {
  mountId: string;
  name: string;
  entities: readonly StateEntity[];
}

export function stateRows(readings: readonly EntityReading[]): StateRow[] {
  return readings.flatMap((reading) =>
    reading.entities.map((entity) => rowOf(entity, reading.name, reading.mountId)),
  );
}

/** How the panel narrows the list. Every field is optional and an empty one filters nothing. */
export interface StateFilter {
  prefix: string;
  type: string;
  tag: string;
}

export function matches(row: StateRow, filter: StateFilter): boolean {
  if (filter.prefix !== "" && !row.address.startsWith(filter.prefix)) return false;
  if (filter.type !== "" && row.type !== filter.type) return false;
  if (filter.tag !== "" && !row.tags.includes(filter.tag)) return false;
  return true;
}

export interface AddressGroup {
  address: string;
  rows: StateRow[];
}

/** One group per address, in address order, so two activities at one address read as one. */
export function byAddress(rows: readonly StateRow[]): AddressGroup[] {
  const held = new Map<string, StateRow[]>();
  for (const row of rows) {
    const found = held.get(row.address);
    if (found === undefined) held.set(row.address, [row]);
    else found.push(row);
  }
  return [...held.entries()]
    .map(([address, group]) => ({ address, rows: group }))
    .sort((left, right) => left.address.localeCompare(right.address));
}

/** Every entity type present, for the type filter. */
export function typesIn(rows: readonly StateRow[]): string[] {
  return [...new Set(rows.map((one) => one.type))].sort((a, b) => a.localeCompare(b));
}

/** Every tag present, for the tag filter. */
export function tagsIn(rows: readonly StateRow[]): string[] {
  return [...new Set(rows.flatMap((one) => one.tags))].sort((a, b) => a.localeCompare(b));
}
